/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from 'react';
import { Box, Typography } from '@mui/material';
import { useSnackbar } from 'notistack';
import * as eqpAgentApi from '../../lib/api/eqpAgent';
import EqpAgentListPanel from '../../components/eqpmng/EqpAgentListPanel';
import EqpAgentFormDialog from '../../components/eqpmng/EqpAgentFormDialog';
import EqpAgentTokenDialog from '../../components/eqpmng/EqpAgentTokenDialog';
import EqpAgentDetailDialog from '../../components/eqpmng/EqpAgentDetailDialog';
import EqpAgentInstallerDialog from '../../components/eqpmng/EqpAgentInstallerDialog';
import EqpAgentDiscoveryDialog from '../../components/eqpmng/EqpAgentDiscoveryDialog';
import BreadcrumbsContainer from '../common/breadcurmbs/BreadcrumbsContainer';

const REFRESH_MS = 30000; // 온라인/오프라인·마지막 접속은 에이전트가 주기적으로 갱신하므로 화면도 같이 돈다

const EqpAgentMngContainer = () => {
  const { enqueueSnackbar } = useSnackbar();

  const [list, setList]       = useState([]);
  const [loading, setLoading] = useState(false);
  const [tick, setTick]       = useState(0);

  const [formOpen, setFormOpen]       = useState(false);
  const [detailOpen, setDetailOpen]   = useState(false);
  const [selectedAgent, setSelectedAgent] = useState(null);

  const [tokenDialog, setTokenDialog] = useState({ open: false, token: '', agentNm: '', agentUuid: '' });
  // agentUuid가 있으면 "이 에이전트용", 없으면 공용 설치 파일만 보여준다(EqpAgentInstallerDialog 참고)
  const [installerDialog, setInstallerDialog] = useState({ open: false, agentUuid: null, agentNm: null });
  const [discoveryDialog, setDiscoveryDialog] = useState({ open: false, agentUuid: null, agentNm: null });

  useEffect(() => {
    const fetchList = async (showSpinner) => {
      if (showSpinner) setLoading(true);
      try {
        const res = await eqpAgentApi.getAgentList();
        setList(eqpAgentApi.extractAgentList(res));
      } catch (e) {
        enqueueSnackbar('에이전트 목록 조회 실패', { variant: 'error' });
      } finally {
        if (showSpinner) setLoading(false);
      }
    };
    fetchList(true);

    const timer = setInterval(() => fetchList(false), REFRESH_MS);
    return () => clearInterval(timer);
  }, [tick]);

  const handleSave = async (values, agentData) => {
    try {
      if (agentData) {
        await eqpAgentApi.editAgent(agentData.agentUuid, values);
        enqueueSnackbar('에이전트 정보를 수정했습니다.', { variant: 'success' });
        setFormOpen(false);
        setSelectedAgent(null);
        setTick((t) => t + 1);
      } else {
        const res = await eqpAgentApi.addAgent(values);
        enqueueSnackbar('에이전트를 등록했습니다.', { variant: 'success' });
        setFormOpen(false);
        setSelectedAgent(null);
        setTick((t) => t + 1);
        // 응답 봉투가 {info:{...token...}, result}라 info 밑에서 꺼내야 한다(기존 코드가 top-level을 보고 있던 버그 수정)
        setTokenDialog({
          open: true,
          token: res.data?.info?.token,
          agentNm: values.agentNm,
          agentUuid: res.data?.info?.agentUuid,
        });
      }
    } catch (e) {
      enqueueSnackbar('저장에 실패했습니다.', { variant: 'error' });
    }
  };

  const handleReissueToken = async (agentData) => {
    try {
      const res = await eqpAgentApi.reissueAgentToken(agentData.agentUuid);
      enqueueSnackbar('토큰을 재발급했습니다.', { variant: 'success' });
      setFormOpen(false);
      setSelectedAgent(null);
      setTokenDialog({
        open: true,
        token: res.data?.info?.token,
        agentNm: agentData.agentNm,
        agentUuid: agentData.agentUuid,
      });
    } catch (e) {
      enqueueSnackbar('토큰 재발급에 실패했습니다.', { variant: 'error' });
    }
  };

  const handleDelete = async (agentData) => {
    try {
      await eqpAgentApi.removeAgent(agentData.agentUuid);
      enqueueSnackbar('에이전트를 삭제했습니다.', { variant: 'success' });
      setFormOpen(false);
      setSelectedAgent(null);
      setTick((t) => t + 1);
    } catch (e) {
      enqueueSnackbar('삭제에 실패했습니다.', { variant: 'error' });
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <BreadcrumbsContainer title="기준정보/에이전트 관리" />
      <Typography variant="h5" fontWeight={700} mb={2}>에이전트 관리</Typography>

      <EqpAgentListPanel
        list={list}
        loading={loading}
        onAdd={() => { setSelectedAgent(null); setFormOpen(true); }}
        onRowClick={(row) => { setSelectedAgent(row); setDetailOpen(true); }}
        onOpenInstaller={() => setInstallerDialog({ open: true, agentUuid: null, agentNm: null })}
      />

      <EqpAgentDetailDialog
        open={detailOpen}
        agentData={selectedAgent}
        onClose={() => { setDetailOpen(false); setSelectedAgent(null); }}
        onEdit={(agentData) => { setDetailOpen(false); setSelectedAgent(agentData); setFormOpen(true); }}
        onOpenInstaller={(agentData) => {
          setDetailOpen(false);
          setInstallerDialog({ open: true, agentUuid: agentData.agentUuid, agentNm: agentData.agentNm });
        }}
        onOpenDiscovery={(agentData) => {
          setDetailOpen(false);
          setDiscoveryDialog({ open: true, agentUuid: agentData.agentUuid, agentNm: agentData.agentNm });
        }}
      />

      <EqpAgentFormDialog
        open={formOpen}
        agentData={selectedAgent}
        onClose={() => { setFormOpen(false); setSelectedAgent(null); }}
        onSave={handleSave}
        onReissueToken={handleReissueToken}
        onDelete={handleDelete}
      />

      <EqpAgentTokenDialog
        open={tokenDialog.open}
        token={tokenDialog.token}
        agentNm={tokenDialog.agentNm}
        onClose={() => setTokenDialog({ open: false, token: '', agentNm: '', agentUuid: '' })}
        onOpenInstaller={() => {
          setInstallerDialog({ open: true, agentUuid: tokenDialog.agentUuid, agentNm: tokenDialog.agentNm });
          setTokenDialog({ open: false, token: '', agentNm: '', agentUuid: '' });
        }}
      />

      <EqpAgentInstallerDialog
        open={installerDialog.open}
        agentUuid={installerDialog.agentUuid}
        agentNm={installerDialog.agentNm}
        onClose={() => setInstallerDialog({ open: false, agentUuid: null, agentNm: null })}
      />

      <EqpAgentDiscoveryDialog
        open={discoveryDialog.open}
        agentUuid={discoveryDialog.agentUuid}
        agentNm={discoveryDialog.agentNm}
        onClose={() => setDiscoveryDialog({ open: false, agentUuid: null, agentNm: null })}
      />
    </Box>
  );
};

export default EqpAgentMngContainer;
